import {
  Component,
  ViewEncapsulation,
  OnDestroy,
  AfterViewInit,
  HostListener,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NgTemplateOutlet } from '@angular/common';

import { TemplateService } from '../../../services/template/template.service';
import { AuthService } from '../../../services/auth/auth.service';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import {
  selectIsAuthenticated,
  selectCurrentPortal,
  selectUserRoles,
} from '../../../ngrx/auth/auth.selectors';
import * as AuthActions from '../../../ngrx/auth/auth.actions';
import { Router } from '@angular/router';
import { UserService } from '../../../services/user/user.service';
import { ModalService } from '../../../services/modal/modal.service';
import { MatIconModule } from '@angular/material/icon';
import { map } from 'rxjs';
import { SiteCacheService } from '../../../services/site-cache/site-cache.service';

@Component({
  selector: 'app-nav',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, LetDirective, MatIconModule, NgTemplateOutlet],
  templateUrl: './nav.component.html',
  styleUrl: './nav.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class NavComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mobileNav') private mobileNav?: ElementRef<HTMLElement>;

  isMenuOpen: boolean = false;

  // Get auth state directly from selectors
  isAuthenticated$ = this.store.select(selectIsAuthenticated);
  currentPortal$ = this.store.select(selectCurrentPortal);
  hasUddansvarligRole$ = this.store
    .select(selectUserRoles)
    .pipe(map((roles) => roles.includes('uddansvarlig')));
  hasMultipleAccountTypes$ = this.userService.getAccountTypes().pipe(
    map((types) => {
      return Array.isArray(types) && types.length > 1;
    })
  );

  constructor(
    public templateService: TemplateService,
    private store: Store,
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private modalService: ModalService,
    private siteCacheService: SiteCacheService
  ) {}

  ngAfterViewInit(): void {
    const overlay = this.mobileNav?.nativeElement;
    if (overlay && overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }
  }

  toggleMenu() {
    // Only allow menu toggle on mobile/tablet screens
    if (window.innerWidth <= 1320) {
      this.isMenuOpen = !this.isMenuOpen;
      this.syncNavOpenClass();
    }
  }

  closeMenuOnMobile() {
    // Only close menu on mobile/tablet screens
    if (window.innerWidth <= 1320) {
      this.isMenuOpen = false;
      this.syncNavOpenClass();
    }
  }

  logout() {
    this.closeMenuOnMobile(); // Close menu before logout (only on mobile)
    this.store.dispatch(AuthActions.logout());
  }

  openAccountTypeSwitcher() {
    this.modalService.showModalByName('switch_impersonate');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (window.innerWidth <= 1320 && this.isMenuOpen) {
      const overlay = this.mobileNav?.nativeElement;
      const burgerMenu = document.querySelector('.burger-menu');
      const clickedElement = event.target as HTMLElement;

      if (
        overlay &&
        burgerMenu &&
        !overlay.contains(clickedElement) &&
        !burgerMenu.contains(clickedElement)
      ) {
        this.closeMenuOnMobile();
      }
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any) {
    // Reset menu state when switching to desktop view
    if (window.innerWidth > 1320 && this.isMenuOpen) {
      this.isMenuOpen = false;
      this.syncNavOpenClass();
    }
  }

  ngOnDestroy(): void {
    this.isMenuOpen = false;
    this.syncNavOpenClass();
    this.mobileNav?.nativeElement.remove();
  }

  private syncNavOpenClass(): void {
    const open = this.isMenuOpen && window.innerWidth <= 1320;
    document.documentElement.classList.toggle('duna-nav-open', open);
    document.body.classList.toggle('duna-nav-open', open);
  }

  /**
   * Navigate to home with complete cache flush
   * Ensures users get the latest version when clicking the logo
   */
  async navigateHomeWithCacheRefresh() {

    // Always perform complete cache flush when logo is clicked
    // This ensures users get absolutely fresh content
    await this.siteCacheService.navigateHomeWithCacheFlush();
  }
}
